import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";

const UserProfile = () => {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [userPhotos, setUserPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("albums");
  const [albums, setAlbums] = useState([]);
  const [selectedAlbum, setSelectedAlbum] = useState(null);
  const [isCurrentUser, setIsCurrentUser] = useState(false);
  const [friendStatus, setFriendStatus] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isEditingAlbumName, setIsEditingAlbumName] = useState(false);
  const [newAlbumName, setNewAlbumName] = useState("");
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [isEditingTags, setIsEditingTags] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState("");
  const [tagSearchResults, setTagSearchResults] = useState([]);
  const [taggedPeople, setTaggedPeople] = useState([]);
  const [savingTags, setSavingTags] = useState(false);
  const [isManagingPhotos, setIsManagingPhotos] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [photosToDelete, setPhotosToDelete] = useState([]);
  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [isPublic, setIsPublic] = useState(false);
  const [showOnProfile, setShowOnProfile] = useState(false);
  const [isGeneratingLink, setIsGeneratingLink] = useState(false);
  const [shareId, setShareId] = useState(null);
  const [shareUrl, setShareUrl] = useState(null);
  const [copied, setCopied] = useState(false); // Add this line to fix the errors
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const linkRef = useRef(null);

  useEffect(() => {
    // Reset state when userID changes
    setLoading(true);
    setError(null);
    setUser(null);
    setUserPhotos([]);
    setRetryCount(0);

    fetchData();
  }, [userId]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get current user first
      const currentUserResponse = await axios.get(
        `${process.env.REACT_APP_DATABASE_URL}/api/users/me`
      );
      const currentUserId = currentUserResponse.data._id;
      const isOwnProfile =
        userId === currentUserId ||
        userId === currentUserResponse.data.username;

      setIsCurrentUser(isOwnProfile);

      // Get profile user
      let userResponse;
      let targetUserId;

      if (isOwnProfile) {
        userResponse = currentUserResponse;
        targetUserId = currentUserId;
      } else {
        userResponse = await axios.get(
          `${process.env.REACT_APP_DATABASE_URL}/api/users/${userId}`
        );
        targetUserId = userResponse.data._id;
      }

      setUser(userResponse.data);

      // Get user photos
      const photosResponse = await axios.get(
        `${process.env.REACT_APP_DATABASE_URL}/api/photos/user/${targetUserId}`
      );
      setUserPhotos(photosResponse.data);

      // Get albums
      const albumsResponse = await axios.get(
        `${process.env.REACT_APP_DATABASE_URL}/api/photos/albums`
      );
      setAlbums(albumsResponse.data);

      // Check friend status if not current user
      if (!isOwnProfile) {
        setFriendStatus(userResponse.data.friendshipStatus);
      }
    } catch (err) {
      console.error("Error fetching profile data:", err);

      // If the user profile couldn't be found with the userId, try again as it might be a username
      if (err.response?.status === 404 && retryCount < 1) {
        setRetryCount((prevCount) => prevCount + 1);
        // If userId looks like an ObjectId but isn't found, try searching by username
        navigate(`/profile/@${userId}`, { replace: true });
      } else {
        setError(
          "Failed to load profile. Please check the username and try again."
        );
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendFriendRequest = async () => {
    try {
      await axios.post(
        `${process.env.REACT_APP_DATABASE_URL}/api/users/friends/request`,
        { userId: user._id }
      );
      setFriendStatus("pending-sent");
    } catch (err) {
      console.error("Error sending friend request:", err);
      setError("Failed to send friend request");
    }
  };

  const handleRespondToRequest = async (accept) => {
    try {
      // Find the request ID
      const requestsResponse = await axios.get(
        `${process.env.REACT_APP_DATABASE_URL}/api/users/friends/requests`
      );
      const request = requestsResponse.data.find(
        (request) => request.from.id === user._id
      );

      if (request) {
        await axios.post(
          `${process.env.REACT_APP_DATABASE_URL}/api/users/friends/respond`,
          {
            requestId: request.id,
            accept,
          }
        );

        setFriendStatus(accept ? "friends" : "not-friends");
      }
    } catch (err) {
      console.error("Error responding to friend request:", err);
      setError("Failed to respond to friend request");
    }
  };

  const handleRefresh = () => {
    setRetryCount(0); // Reset retry count
    fetchData();
  };

  // Function to get all photos from all albums
  const getAllPhotos = () => {
    if (!userPhotos || userPhotos.length === 0) return [];

    const allPhotos = [];
    userPhotos.forEach((album) => {
      if (album.photos && album.photos.length > 0) {
        album.photos.forEach((photo) => {
          allPhotos.push({
            ...photo,
            albumName: album.albumName,
            albumId: album.batchId,
            date: album.date,
          });
        });
      }
    });

    return allPhotos;
  };

  // Start editing the album name
  const handleEditAlbumName = () => {
    setNewAlbumName(selectedPhoto.albumName || "");
    setIsEditingAlbumName(true);
  };

  // Save the updated album name
  const handleSaveAlbumName = async () => {
    try {
      await axios.patch(
        `${process.env.REACT_APP_DATABASE_URL}/api/photos/albums/${selectedPhoto.albumId}`,
        { albumName: newAlbumName }
      );

      // Update the selected photo with new album name
      setSelectedPhoto({
        ...selectedPhoto,
        albumName: newAlbumName,
      });

      // Update the userPhotos array to show the updated name in the UI
      setUserPhotos((prev) =>
        prev.map((album) =>
          album.batchId === selectedPhoto.albumId
            ? { ...album, albumName: newAlbumName }
            : album
        )
      );

      setIsEditingAlbumName(false);
    } catch (error) {
      console.error("Error updating album name:", error);
    }
  };

  // Cancel editing
  const handleCancelEditAlbumName = () => {
    setIsEditingAlbumName(false);
  };

  // Handle album deletion
  const handleDeleteAlbum = async () => {
    if (!selectedPhoto || !selectedPhoto.albumId) return;

    setIsDeleting(true);
    setDeleteError(null);

    try {
      await axios.delete(
        `${process.env.REACT_APP_DATABASE_URL}/api/photos/albums/${selectedPhoto.albumId}`
      );

      // Close the modal
      setSelectedPhoto(null);

      // Remove the deleted album from the UI
      setUserPhotos((prev) =>
        prev.filter((album) => album.batchId !== selectedPhoto.albumId)
      );

      // Reset states
      setIsConfirmingDelete(false);
      setIsDeleting(false);
    } catch (error) {
      console.error("Error deleting album:", error);
      setDeleteError("Failed to delete album. Please try again.");
      setIsDeleting(false);
    }
  };

  const handleSearchFriends = async (query) => {
    if (!query.trim()) {
      setTagSearchResults([]);
      return;
    }
    try {
      const { data } = await axios.get(
        `${
          process.env.REACT_APP_DATABASE_URL
        }/api/users/search?q=${encodeURIComponent(query)}`
      );
      const filtered = data.filter(
        (u) => !taggedPeople.some((p) => p.id === u.id || p._id === u._id)
      );
      setTagSearchResults(filtered);
    } catch (err) {
      console.error("Error searching friends:", err);
    }
  };

  // Function to add a tag
  const handleAddTag = (friend) => {
    // Check if already tagged
    if (
      taggedPeople.some(
        (person) => person.id === friend.id || person._id === friend._id
      )
    ) {
      return;
    }

    // Add to tagged people with username
    setTaggedPeople([
      ...taggedPeople,
      {
        id: friend.id || friend._id,
        _id: friend._id || friend.id,
        name: friend.name,
        username: friend.username, // Make sure username is included
        profilePhotoUrl: friend.profilePhotoUrl,
      },
    ]);

    // Clear search
    setTagSearchQuery("");
    setTagSearchResults([]);
  };

  // Function to remove a tag
  const handleRemoveTag = (index) => {
    const updatedTags = [...taggedPeople];
    updatedTags.splice(index, 1);
    setTaggedPeople(updatedTags);
  };

  // Function to save tags
  const handleSaveTags = async () => {
    setSavingTags(true);

    try {
      await axios.post(
        `${process.env.REACT_APP_DATABASE_URL}/api/photos/${selectedPhoto.id}/tags`,
        {
          tags: taggedPeople.map((person) => ({
            userId: person.id || person._id,
            name: person.name,
          })),
        }
      );

      // Create a copy of allPhotos and update the current photo's tags
      const updatedAllPhotos = [...selectedPhoto.allPhotos];
      updatedAllPhotos[selectedPhoto.photoIndex] = {
        ...updatedAllPhotos[selectedPhoto.photoIndex],
        tags: taggedPeople,
      };

      // Update the selected photo with new tags AND updated allPhotos
      setSelectedPhoto({
        ...selectedPhoto,
        tags: taggedPeople,
        allPhotos: updatedAllPhotos,
      });

      // Also update userPhotos state to ensure everything stays in sync
      setUserPhotos((prev) =>
        prev.map((album) =>
          album.batchId === selectedPhoto.albumId
            ? {
                ...album,
                photos: album.photos.map((photo) =>
                  photo.id === selectedPhoto.id
                    ? { ...photo, tags: taggedPeople }
                    : photo
                ),
              }
            : album
        )
      );

      // Close the modal
      setIsEditingTags(false);
    } catch (error) {
      console.error("Error saving tags:", error);
    } finally {
      setSavingTags(false);
    }
  };

  // Function to handle file selection
  const handleFileSelect = (e) => {
    const newFiles = Array.from(e.target.files);
    const validFiles = newFiles.filter((file) =>
      file.type.startsWith("image/")
    );
    setSelectedFiles((prev) => [...prev, ...validFiles]);
  };

  // Function to remove a file from selection
  const removeFile = (indexToRemove) => {
    setSelectedFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  // Function to toggle photo selection for deletion
  const togglePhotoForDeletion = (photoId) => {
    if (photosToDelete.includes(photoId)) {
      setPhotosToDelete((prev) => prev.filter((id) => id !== photoId));
    } else {
      setPhotosToDelete((prev) => [...prev, photoId]);
    }
  };

  // Function to upload photos to the album
  const handleUploadPhotos = async () => {
    if (selectedFiles.length === 0) return;

    setUploading(true);
    setUploadProgress(0);

    try {
      const formData = new FormData();
      selectedFiles.forEach((file) => {
        formData.append("photos", file);
      });

      // Add album details
      formData.append("albumName", selectedPhoto.albumName || "");
      formData.append("location", selectedPhoto.location || "");
      formData.append("uploadBatch", selectedPhoto.albumId);

      const response = await axios.post(
        `${process.env.REACT_APP_DATABASE_URL}/api/photos/upload`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress(percentCompleted);
          },
        }
      );

      // Update the UI with new photos
      const updatedAlbums = [...userPhotos];
      const albumIndex = updatedAlbums.findIndex(
        (album) => album.batchId === selectedPhoto.albumId
      );

      if (albumIndex !== -1) {
        // Add new photos to the album
        updatedAlbums[albumIndex].photos = [
          ...updatedAlbums[albumIndex].photos,
          ...response.data.photos,
        ];

        setUserPhotos(updatedAlbums);

        // Update the selected photo's allPhotos array
        setSelectedPhoto({
          ...selectedPhoto,
          allPhotos: [...selectedPhoto.allPhotos, ...response.data.photos],
          totalPhotos: selectedPhoto.totalPhotos + response.data.photos.length,
        });
      }

      // Clear selected files
      setSelectedFiles([]);
    } catch (error) {
      console.error("Error uploading photos:", error);
    } finally {
      setUploading(false);
    }
  };

  // Function to delete selected photos
  const handleDeletePhotos = async () => {
    if (photosToDelete.length === 0) return; // This return is correct

    setDeleteInProgress(true);

    try {
      // Delete each photo
      for (const photoId of photosToDelete) {
        await axios.delete(
          `${process.env.REACT_APP_DATABASE_URL}/api/photos/${photoId}`
        );
      }

      // Update the UI by removing deleted photos
      const updatedAlbums = [...userPhotos];
      const albumIndex = updatedAlbums.findIndex(
        (album) => album.batchId === selectedPhoto.albumId
      );

      if (albumIndex !== -1) {
        // Filter out deleted photos
        updatedAlbums[albumIndex].photos = updatedAlbums[
          albumIndex
        ].photos.filter((photo) => !photosToDelete.includes(photo.id));

        setUserPhotos(updatedAlbums);

        // Update selected photo
        const updatedAllPhotos = selectedPhoto.allPhotos.filter(
          (photo) => !photosToDelete.includes(photo.id)
        );

        if (updatedAllPhotos.length === 0) {
          // Close modals if no photos left
          setIsManagingPhotos(false);
          setSelectedPhoto(null);
        } else {
          setSelectedPhoto({
            ...selectedPhoto,
            allPhotos: updatedAllPhotos,
            totalPhotos: updatedAllPhotos.length,
            photoIndex: Math.min(
              selectedPhoto.photoIndex,
              updatedAllPhotos.length - 1
            ),
          });
        }
      }

      // Clear selected photos for deletion
      setPhotosToDelete([]);
    } catch (error) {
      console.error("Error deleting photos:", error);
      alert("Failed to delete one or more photos. Please try again.");
    } finally {
      setDeleteInProgress(false);
    }
  };

  const handleVisibilityChange = async (setting, value) => {
    try {
      // Convert boolean to string if needed - some APIs expect string "true"/"false"
      const dataValue = typeof value === "boolean" ? value : Boolean(value);

      // Create payload with both the setting to change AND the required albumName field
      const payload = {
        [setting]: dataValue,
        albumName: selectedPhoto.albumName || "Unnamed Album", // Include album name to satisfy API requirement
      };
      console.log("Sending update request:", payload);

      // Update setting in the backend
      const response = await axios.patch(
        `${process.env.REACT_APP_DATABASE_URL}/api/photos/albums/${selectedPhoto.albumId}`,
        payload
      );

      console.log("Server response:", response.data);

      // Update the selected photo state and UI
      setSelectedPhoto({
        ...selectedPhoto,
        [setting]: value,
      });

      // Update albums in userPhotos state
      setUserPhotos((prev) =>
        prev.map((album) =>
          album.batchId === selectedPhoto.albumId
            ? { ...album, [setting]: value }
            : album
        )
      );

      // For public access toggle, initialize related states
      if (setting === "isPublic") {
        setIsPublic(value);
        if (!value) {
          setShareId(null);
          setShareUrl(null);
        }
      } else if (setting === "showOnProfile") {
        setShowOnProfile(value);
      }
    } catch (error) {
      console.error(`Error updating ${setting}:`, error);
      // Show the specific error message from the API
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        alert(`Error: ${error.response.data.message}`);
      } else {
        alert(`Failed to update ${setting}. Please try again.`);
      }
    }
  };

  // Function to generate a public link
  const handleGeneratePublicLink = async () => {
    setIsGeneratingLink(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_DATABASE_URL}/api/photos/share`,
        { albumId: selectedPhoto.albumId }
      );

      const { shareId, shareUrl } = response.data;

      // Update states
      setShareId(shareId);
      setShareUrl(shareUrl);

      // Update the selected photo
      setSelectedPhoto({
        ...selectedPhoto,
        shareId: shareId,
      });

      // Update albums in userPhotos state
      setUserPhotos((prev) =>
        prev.map((album) =>
          album.batchId === selectedPhoto.albumId
            ? { ...album, shareId: shareId }
            : album
        )
      );
    } catch (error) {
      console.error("Error generating public link:", error);
    } finally {
      setIsGeneratingLink(false);
    }
  };

  // Function to copy link to clipboard
  const copyToClipboard = () => {
    if (linkRef.current) {
      navigator.clipboard
        .writeText(linkRef.current.value)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch((err) => {
          console.error("Failed to copy: ", err);
        });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="mb-4">
            <svg
              className="animate-spin h-12 w-12 text-purple-600 mx-auto"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
          </div>
          <p className="text-gray-600 text-lg">Loading profile...</p>
        </div>
      </div>
    );
  }

  if (error || !user) {
    return (
      <div className="max-w-4xl mx-auto p-6 pt-10 text-center">
        <div className="bg-white rounded-xl shadow-lg p-8">
          <div className="text-red-500 mb-4">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 mx-auto"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Profile Not Found
          </h2>
          <p className="text-gray-600 mb-6">
            {error ||
              "The user profile you're looking for doesn't exist or is not accessible."}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => fetchData()}
              className="px-6 py-3 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700"
            >
              Try Again
            </button>
            <Link
              to="/feed"
              className="px-6 py-3 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300"
            >
              Back to Feed
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4 pt-6">
      {/* Profile header */}
      <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
        <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
          <div className="w-32 h-32 rounded-full bg-purple-200 flex items-center justify-center overflow-hidden">
            {user.profilePhotoUrl ? (
              <img
                src={user.profilePhotoUrl}
                alt={user.name}
                className="h-full w-full object-cover"
              />
            ) : (
              <span className="text-5xl font-medium text-purple-700">
                {user.name.charAt(0).toUpperCase()}
              </span>
            )}
          </div>

          <div className="flex-1 text-center md:text-left">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {user.name}
            </h1>
            {user.username && (
              <div className="text-gray-600 mb-4">@{user.username}</div>
            )}

            <div className="flex flex-wrap justify-center md:justify-start items-center gap-4 mb-4">
              <div className="flex items-center text-gray-600">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span>{userPhotos.length} Albums</span>
              </div>

              <div className="flex items-center text-gray-600">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
                <span>{user.friends?.length || 0} Friends</span>
              </div>

              <div className="flex items-center text-gray-600">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-2"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span>
                  Joined{" "}
                  {new Date(user.createdAt).toLocaleDateString(undefined, {
                    year: "numeric",
                    month: "long",
                  })}
                </span>
              </div>
            </div>

            {!isCurrentUser && (
              <div className="mt-4">
                {friendStatus === "friends" && (
                  <span className="px-4 py-2 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                    You are friends
                  </span>
                )}

                {friendStatus === "pending-sent" && (
                  <span className="px-4 py-2 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                    Friend request sent
                  </span>
                )}

                {friendStatus === "pending-received" && (
                  <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                    <span className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
                      Friend request received
                    </span>
                    <button
                      onClick={() => handleRespondToRequest(true)}
                      className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full text-sm font-medium hover:from-purple-700 hover:to-pink-700"
                    >
                      Accept
                    </button>
                    <button
                      onClick={() => handleRespondToRequest(false)}
                      className="px-4 py-2 bg-gray-200 text-gray-800 rounded-full text-sm font-medium hover:bg-gray-300"
                    >
                      Decline
                    </button>
                  </div>
                )}

                {friendStatus === "not-friends" && (
                  <button
                    onClick={handleSendFriendRequest}
                    className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full text-sm font-medium hover:from-purple-700 hover:to-pink-700"
                  >
                    Add Friend
                  </button>
                )}
              </div>
            )}

            {isCurrentUser && (
              <div className="mt-4">
                <Link
                  to="/upload"
                  className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-full shadow-md hover:from-purple-700 hover:to-pink-700 hover:shadow-lg transition-all transform hover:-translate-y-0.5"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 inline mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0l-4 4m4-4v12"
                    />
                  </svg>
                  Upload Photos
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tab navigation - simplified to just show Albums and Friends */}
      <div className="mb-6 border-b border-gray-200">
        <div className="flex space-x-8">
          <button
            className={`pb-4 px-1 ${
              activeTab === "albums"
                ? "border-b-2 border-purple-600 text-purple-600 font-medium"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("albums")}
          >
            Albums
          </button>
          <button
            className={`pb-4 px-1 ${
              activeTab === "friends"
                ? "border-b-2 border-purple-600 text-purple-600 font-medium"
                : "text-gray-500 hover:text-gray-700"
            }`}
            onClick={() => setActiveTab("friends")}
          >
            Friends
          </button>
        </div>
      </div>

      {/* Main content based on active tab */}
      <div>
        {/* Albums Tab - default view */}
        {activeTab === "albums" && (
          <div>
            {userPhotos.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-16 w-16 mx-auto text-gray-400 mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <h3 className="text-xl font-medium text-gray-900 mb-2">
                  No Albums Found
                </h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  {isCurrentUser
                    ? "You haven't created any albums yet. Add album details when uploading photos."
                    : `${user.name} hasn't created any albums yet.`}
                </p>

                {isCurrentUser && (
                  <Link
                    to="/upload"
                    className="mt-6 inline-block px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-pink-700"
                  >
                    Upload Photos
                  </Link>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
                {userPhotos.map((album) => (
                  <div
                    key={album.batchId}
                    className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => {
                      if (album.photos && album.photos.length > 0) {
                        setSelectedPhoto({
                          ...album.photos[0],
                          albumId: album.batchId,
                          albumName: album.albumName,
                          location: album.location,
                          photoIndex: 0,
                          totalPhotos: album.photos.length,
                          allPhotos: album.photos,
                          isPublic: album.isPublic || false,
                          showOnProfile: album.showOnProfile || false,
                          shareId: album.shareId || null,
                        });
                      }
                    }}
                  >
                    {album.photos && album.photos.length > 0 && (
                      <div className="aspect-square overflow-hidden relative">
                        <img
                          src={album.photos[0].url}
                          alt=""
                          className="w-full h-full object-cover"
                        />
                        {album.photos.length > 1 && (
                          <div className="absolute top-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-md">
                            +{album.photos.length - 1}
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4">
                          <h3 className="text-white font-medium truncate">
                            {album.albumName || "Unnamed Album"}
                          </h3>
                          {album.location && (
                            <div className="text-white/90 text-sm flex items-center">
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-3 w-3 mr-1"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                              </svg>
                              {album.location}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    <div className="p-3">
                      <div className="flex justify-between items-center text-sm text-gray-500">
                        <span>
                          {album.photos ? album.photos.length : 0}{" "}
                          {album.photos && album.photos.length === 1
                            ? "photo"
                            : "photos"}
                        </span>
                        <span>
                          {new Date(album.date).toLocaleDateString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Friends Tab */}
        {activeTab === "friends" && (
          <div>
            {!user.friends || user.friends.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl shadow-lg">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-16 w-16 mx-auto text-gray-400 mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                  />
                </svg>
                <h3 className="text-xl font-medium text-gray-900 mb-2">
                  No Friends Yet
                </h3>
                <p className="text-gray-600 max-w-md mx-auto">
                  {isCurrentUser
                    ? "You haven't added any friends yet. Search for friends using the search bar above."
                    : `${user.name} hasn't added any friends yet.`}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
                {user.friends.map((friend) => (
                  <Link
                    key={friend._id}
                    to={`/profile/${friend.username || friend._id}`}
                    className="bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col items-center p-4"
                  >
                    <div className="h-16 w-16 rounded-full bg-purple-200 flex items-center justify-center overflow-hidden mb-3">
                      {friend.profilePhotoUrl ? (
                        <img
                          src={friend.profilePhotoUrl}
                          alt={friend.name}
                          className="h-full w-full object-cover"
                        />
                      ) : (
                        <span className="text-xl font-medium text-purple-700">
                          {friend.name.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <h3 className="font-medium text-gray-900 text-sm text-center truncate w-full">
                      {friend.name}
                    </h3>
                    {friend.username && (
                      <p className="text-xs text-gray-500 text-center truncate w-full">
                        @{friend.username}
                      </p>
                    )}
                  </Link>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal for viewing individual photos with navigation arrows */}
      {selectedPhoto && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90 p-4"
          onClick={() => setSelectedPhoto(null)}
        >
          <div
            className="max-w-6xl w-full max-h-[90vh] relative overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Photo navigation area */}
            <div className="relative flex items-center justify-center">
              {/* Left arrow for previous photo */}
              {selectedPhoto.photoIndex > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex = selectedPhoto.photoIndex - 1;
                    setSelectedPhoto({
                      ...selectedPhoto.allPhotos[newIndex],
                      albumId: selectedPhoto.albumId,
                      albumName: selectedPhoto.albumName,
                      location: selectedPhoto.location,
                      photoIndex: newIndex,
                      totalPhotos: selectedPhoto.totalPhotos,
                      allPhotos: selectedPhoto.allPhotos,
                      isPublic: selectedPhoto.isPublic,
                      showOnProfile: selectedPhoto.showOnProfile,
                      shareId: selectedPhoto.shareId,
                    });
                  }}
                  className="absolute left-4 bg-black/50 text-white rounded-full p-4 hover:bg-black/70 z-10 transition-all hover:scale-110"
                  aria-label="Previous photo"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M15 19l-7-7 7-7"
                    />
                  </svg>
                </button>
              )}

              {/* Main photo display */}

              <img
                src={selectedPhoto.url}
                alt=""
                className="w-full max-h-[70vh] object-contain"
              />

              {/* Download button for friends/owner */}
              <div className="absolute bottom-4 right-4">
                <button className="flex items-center px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-pink-600 rounded-lg hover:from-purple-700 hover:to-pink-700">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0l-4 4m4-4v12"
                    />
                  </svg>
                  Download
                </button>
              </div>

              {/* Tags */}
              <div className="absolute bottom-3 left-8">
                {selectedPhoto.tags && selectedPhoto.tags.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-medium text-white mb-2">
                      In this photo:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedPhoto.tags.map((tag, index) => (
                        <span
                          key={index}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800"
                        >
                          {tag.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right arrow for next photo */}
              {selectedPhoto.photoIndex < selectedPhoto.totalPhotos - 1 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    const newIndex = selectedPhoto.photoIndex + 1;
                    setSelectedPhoto({
                      ...selectedPhoto.allPhotos[newIndex],
                      albumId: selectedPhoto.albumId,
                      albumName: selectedPhoto.albumName,
                      location: selectedPhoto.location,
                      photoIndex: newIndex,
                      totalPhotos: selectedPhoto.totalPhotos,
                      allPhotos: selectedPhoto.allPhotos,
                      isPublic: selectedPhoto.isPublic,
                      showOnProfile: selectedPhoto.showOnProfile,
                      shareId: selectedPhoto.shareId,
                    });
                  }}
                  className="absolute right-4 bg-black/50 text-white rounded-full p-4 hover:bg-black/70 z-10 transition-all hover:scale-110"
                  aria-label="Next photo"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              )}

              {/* Close button */}
              <button
                onClick={() => setSelectedPhoto(null)}
                className="absolute top-4 right-4 bg-black/50 text-white rounded-full p-2 hover:bg-black/70"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Photo details in modal */}
            <div className="p-6 bg-white rounded-b-xl">
              <div className="flex items-center mb-4">
                <div className="h-10 w-10 rounded-full bg-purple-200 flex items-center justify-center overflow-hidden mr-3">
                  {user.profilePhotoUrl ? (
                    <img
                      src={user.profilePhotoUrl}
                      alt={user.name}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <span className="text-lg font-medium text-purple-700">
                      {user.name.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-900">{user.name}</p>
                  {selectedPhoto.albumName && (
                    <p className="text-sm text-gray-500">
                      Album: {selectedPhoto.albumName}
                      {selectedPhoto.location && ` • ${selectedPhoto.location}`}
                    </p>
                  )}
                </div>

                <div className="ml-auto text-sm text-gray-500">
                  {selectedPhoto.photoIndex + 1} / {selectedPhoto.totalPhotos}
                </div>
              </div>

              {/* Album management options - only for album owner */}
              {isCurrentUser && (
                <div className="mb-4 flex flex-wrap gap-2">
                  {isEditingAlbumName ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={newAlbumName}
                        onChange={(e) => setNewAlbumName(e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="Album name"
                        autoFocus
                      />
                      <button
                        onClick={handleSaveAlbumName}
                        className="flex items-center px-3 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-4 w-4 mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        Save
                      </button>
                      <button
                        onClick={handleCancelEditAlbumName}
                        className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleEditAlbumName}
                      className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 003-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                      Edit Album Name
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setIsEditingTags(true);
                      setTaggedPeople(selectedPhoto.tags || []);
                    }}
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"
                      />
                    </svg>
                    Edit Tags
                  </button>

                  <button
                    onClick={() => setIsManagingPhotos(true)}
                    className="flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                    Manage Photos
                  </button>
                  <button
                    onClick={() => setIsConfirmingDelete(true)}
                    className="flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                      />
                    </svg>
                    Delete Album
                  </button>

                  <button
                    onClick={() =>
                      handleVisibilityChange(
                        "showOnProfile",
                        !selectedPhoto.showOnProfile
                      )
                    }
                    className={`flex items-center px-3 py-2 text-sm font-medium ${
                      selectedPhoto.showOnProfile
                        ? "text-green-700 bg-green-100 hover:bg-green-200"
                        : "text-gray-700 bg-gray-100 hover:bg-gray-200"
                    } rounded-lg transition-colors`}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className={`h-4 w-4 mr-2 ${
                        selectedPhoto.showOnProfile
                          ? "text-green-600"
                          : "text-gray-600"
                      }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d={
                          selectedPhoto.showOnProfile
                            ? "M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            : "M3.693 5.304l-1.385 1.385a1 1 0 000 1.414l11.314 11.314a1 1 0 001.414 0l1.385-1.385M4 12a8 8 0 0116 0M21.542 12c-1.274 4.057-5.064 7-9.542 7-1.942 0-3.75-.5-5.318-1.376"
                        }
                      />
                    </svg>
                    Album{" "}
                    {selectedPhoto.showOnProfile
                      ? "Visible on Profile"
                      : "Hidden from Profile"}
                  </button>

                  {/* <button
                    onClick={() => {
                      // First make sure the album is public
                      handleVisibilityChange("isPublic", true);
                      // Then generate the public link
                      handleGeneratePublicLink();
                    }}
                    className="flex items-center px-3 py-2 text-sm font-medium text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4 mr-2 text-blue-600"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                      />
                    </svg>
                    Generate Public Link
                  </button> */}
                </div>
              )}

              {/* Confirmation modal for album deletion */}
              {isConfirmingDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                  <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">
                      Are you sure you want to delete this album?
                    </h3>
                    <p className="text-sm text-gray-600 mb-6">
                      This action cannot be undone.
                    </p>
                    {deleteError && (
                      <p className="text-sm text-red-600 mb-4">{deleteError}</p>
                    )}
                    <div className="flex justify-end gap-4">
                      <button
                        onClick={() => setIsConfirmingDelete(false)}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleDeleteAlbum}
                        disabled={isDeleting}
                        className={`px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 ${
                          isDeleting ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        {isDeleting ? "Deleting..." : "Delete"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tag Editing Modal */}
      {isEditingTags && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Edit Tags</h3>
              <button
                onClick={() => setIsEditingTags(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Search for friends */}
            <div className="mb-4">
              <div className="flex mb-2">
                <input
                  type="text"
                  value={tagSearchQuery}
                  onChange={(e) => {
                    const q = e.target.value;
                    setTagSearchQuery(q);
                    handleSearchFriends(q);
                  }}
                  placeholder="Search by name or @username" // Updated placeholder
                  className="flex-grow px-4 py-2 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <button
                  onClick={() => handleSearchFriends(tagSearchQuery)}
                  className="px-4 py-2 bg-purple-600 text-white rounded-r-lg hover:bg-purple-700"
                >
                  Search
                </button>
              </div>

              {/* Search results - updated with username */}
              {tagSearchResults.length > 0 && (
                <div className="border border-gray-200 rounded-lg max-h-40 overflow-y-auto">
                  {tagSearchResults.map((friend) => (
                    <div
                      key={friend._id || friend.id}
                      className="p-2 hover:bg-gray-50 cursor-pointer flex justify-between items-center"
                      onClick={() => handleAddTag(friend)}
                    >
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-purple-200 flex items-center justify-center overflow-hidden mr-2">
                          {friend.profilePhotoUrl ? (
                            <img
                              src={friend.profilePhotoUrl}
                              alt={friend.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span className="text-sm font-medium text-purple-700">
                              {friend.name.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <div className="font-medium text-sm">
                            {friend.name}
                          </div>
                          {friend.username && (
                            <div className="text-xs text-gray-500">
                              @{friend.username}
                            </div>
                          )}
                        </div>
                      </div>
                      <button className="text-purple-600 hover:text-purple-800">
                        Add
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Tagged people list - updated with username */}
            <div className="mb-4">
              <h4 className="font-medium text-gray-900 mb-2">Tagged People:</h4>
              {taggedPeople.length > 0 ? (
                <div className="space-y-2">
                  {taggedPeople.map((person, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-purple-200 flex items-center justify-center overflow-hidden mr-3">
                          {person.profilePhotoUrl ? (
                            <img
                              src={person.profilePhotoUrl}
                              alt={person.name}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <span className="text-sm font-medium text-purple-700">
                              {person.name.charAt(0).toUpperCase()}
                            </span>
                          )}
                        </div>
                        <div>
                          <div className="font-medium">{person.name}</div>
                          {person.username && (
                            <div className="text-xs text-gray-500">
                              @{person.username}
                            </div>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemoveTag(index)}
                        className="text-red-600 hover:text-red-800 p-2 rounded-lg hover:bg-red-50"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 bg-gray-50 rounded-lg border border-dashed border-gray-300">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="mx-auto h-10 w-10 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z"
                    />
                  </svg>
                  <p className="mt-2 text-sm text-gray-500">
                    No one is tagged in this photo
                  </p>
                </div>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setIsEditingTags(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveTags}
                disabled={savingTags}
                className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 disabled:opacity-50"
              >
                {savingTags ? (
                  <div className="flex items-center">
                    <svg
                      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                    Saving...
                  </div>
                ) : (
                  "Save Tags"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photo Management Modal */}
      {isManagingPhotos && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-900">
                Manage Photos: {selectedPhoto.albumName || "Unnamed Album"}
              </h3>
              <button
                onClick={() => {
                  setIsManagingPhotos(false);
                  setSelectedFiles([]);
                  setPhotosToDelete([]);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>

            {/* Upload new photos section */}
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-3">
                Upload New Photos
              </h4>

              <input
                type="file"
                ref={fileInputRef}
                multiple
                accept="image/*"
                onChange={handleFileSelect}
                className="hidden"
              />

              {selectedFiles.length === 0 ? (
                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-10 w-10 mx-auto text-gray-400 mb-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                    />
                  </svg>
                  <p className="text-gray-500">
                    Click to select photos or drag and drop
                  </p>
                  <p className="text-xs text-gray-400 mt-1">
                    JPG, PNG, GIF up to 10MB
                  </p>
                </div>
              ) : (
                <div>
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-sm text-gray-600">
                      Selected {selectedFiles.length} photo
                      {selectedFiles.length !== 1 ? "s" : ""}
                    </span>
                    <button
                      onClick={() => setSelectedFiles([])}
                      className="text-xs text-red-600 hover:text-red-800"
                    >
                      Clear All
                    </button>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-3">
                    {selectedFiles.map((file, index) => (
                      <div key={index} className="group relative">
                        <div className="aspect-square overflow-hidden rounded-lg bg-gray-100">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Preview ${index}`}
                            className="h-full w-full object-cover"
                          />
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 bg-black bg-opacity-40 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeFile(index);
                              }}
                              className="bg-white rounded-full p-1.5 text-gray-700 hover:text-red-600"
                            >
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-5 w-5"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth="2"
                                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="aspect-square border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center cursor-pointer hover:border-purple-400 hover:bg-purple-50 transition-colors"
                    >
                      <div className="text-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-10 w-10 mx-auto text-gray-400"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M12 6v6m0 0v6m0-6h6m-6 0H6"
                          />
                        </svg>
                        <span className="text-sm text-gray-500 mt-2 block">
                          Add More
                        </span>
                      </div>
                    </div>
                  </div>

                  {uploading ? (
                    <div className="space-y-2">
                      <div className="w-full bg-gray-200 rounded-full h-2.5 overflow-hidden">
                        <div
                          className="bg-gradient-to-r from-purple-600 to-pink-600 h-2.5 rounded-full transition-all duration-300"
                          style={{ width: `${uploadProgress}%` }}
                        ></div>
                      </div>
                      <div className="flex justify-between text-xs text-gray-500">
                        <span>Uploading...</span>
                        <span>{uploadProgress}%</span>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={handleUploadPhotos}
                      className="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg hover:from-purple-700 hover:to-pink-700 w-full"
                    >
                      Upload {selectedFiles.length} Photo
                      {selectedFiles.length !== 1 ? "s" : ""} to Album
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Current photos section */}
            {selectedPhoto.allPhotos && selectedPhoto.allPhotos.length > 0 && (
              <div className="mt-8">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="font-medium text-gray-900">
                    Current Photos ({selectedPhoto.allPhotos.length})
                  </h4>
                  {photosToDelete.length > 0 && (
                    <button
                      onClick={handleDeletePhotos}
                      disabled={deleteInProgress}
                      className="px-3 py-1 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center"
                    >
                      {deleteInProgress ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Deleting...
                        </>
                      ) : (
                        <>
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4 mr-1"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                            />
                          </svg>
                          Delete Selected ({photosToDelete.length})
                        </>
                      )}
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                  {selectedPhoto.allPhotos.map((photo, index) => (
                    <div
                      key={photo.id}
                      className={`group relative rounded-lg overflow-hidden border-2 ${
                        photosToDelete.includes(photo.id)
                          ? "border-red-500"
                          : "border-transparent"
                      }`}
                    >
                      <div className="aspect-square overflow-hidden">
                        <img
                          src={photo.url}
                          alt={`Photo ${index + 1}`}
                          className={`h-full w-full object-cover ${
                            photosToDelete.includes(photo.id)
                              ? "opacity-50"
                              : ""
                          }`}
                        />
                      </div>
                      <div className="absolute inset-0 flex items-center justify-center">
                        <button
                          onClick={() => togglePhotoForDeletion(photo.id)}
                          className={`${
                            photosToDelete.includes(photo.id)
                              ? "bg-red-600 text-white"
                              : "bg-white/80 text-gray-700 opacity-0 group-hover:opacity-100"
                          } rounded-full p-2 transition-all`}
                        >
                          {photosToDelete.includes(photo.id) ? (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M6 18L18 6M6 6l12 12"
                              />
                            </svg>
                          ) : (
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-5 w-5"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => {
                  setIsManagingPhotos(false);
                  setSelectedFiles([]);
                  setPhotosToDelete([]);
                }}
                className="px-4 py-2 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add conditional check for selectedPhoto before rendering Album Visibility section */}
      {isCurrentUser && selectedPhoto && (
        <div className="w-full mt-4 pt-4 border-t border-gray-200">
          <h4 className="font-medium text-gray-900 mb-3">Album Visibility</h4>

          <div className="space-y-3">
            {/* Show on Profile Toggle */}
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={selectedPhoto.showOnProfile}
                onChange={(e) =>
                  handleVisibilityChange("showOnProfile", e.target.checked)
                }
                className="h-5 w-5 text-purple-600 rounded focus:ring-purple-500 border-gray-300"
              />
              <span className="ml-2 text-gray-700">
                Show on Profile (Visible to friends)
              </span>
            </label>

            {/* Public Visibility & Link Generator */}
            <div className="mt-3">
              <label className="flex items-center cursor-pointer mb-2">
                <input
                  type="checkbox"
                  checked={selectedPhoto.isPublic}
                  onChange={(e) =>
                    handleVisibilityChange("isPublic", e.target.checked)
                  }
                  className="h-5 w-5 text-purple-600 rounded focus:ring-purple-500 border-gray-300"
                />
                <span className="ml-2 text-gray-700">
                  Make Public (Anyone with link can view)
                </span>
              </label>

              {/* Public Link Generator */}
              {selectedPhoto.isPublic && (
                <div className="mt-3 bg-purple-50 rounded-lg p-4 border border-purple-200">
                  {selectedPhoto.shareId ? (
                    <div>
                      <div className="flex items-center text-purple-800 mb-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5 mr-1"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth="2"
                            d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                          />
                        </svg>
                        <span className="font-medium">Public Link</span>
                      </div>

                      <div className="relative">
                        <input
                          ref={linkRef}
                          type="text"
                          value={`${window.location.origin}/s/${selectedPhoto.shareId}`}
                          readOnly
                          className="w-full pr-20 py-2 px-3 text-sm border border-purple-200 rounded-lg focus:outline-none bg-white"
                        />
                        <button
                          onClick={copyToClipboard}
                          className="absolute right-1 top-1 bottom-1 px-3 text-sm font-medium bg-purple-600 text-white rounded-md hover:bg-purple-700"
                        >
                          {copied ? "Copied!" : "Copy"}
                        </button>
                      </div>

                      <p className="text-xs text-purple-700 mt-2">
                        Anyone with this link can view the photos without an
                        account
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm text-purple-800 mb-3">
                        Generate a public link to share this album with anyone,
                        even without a PhotoBomb account
                      </p>
                      <button
                        onClick={handleGeneratePublicLink}
                        disabled={isGeneratingLink}
                        className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 text-sm"
                      >
                        {isGeneratingLink ? (
                          <>
                            <svg
                              className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              ></circle>
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              ></path>
                            </svg>
                            Generating Link...
                          </>
                        ) : (
                          <>
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              className="h-4 w-4 mr-1"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                                d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
                              />
                            </svg>
                            Generate Public Link
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserProfile;
